// A bare "2026-09-19" is parsed by JS as UTC midnight, so west of Greenwich it
// renders as Sep 18. The formatters below normalize through toLocalDateInput
// and re-anchor at noon — the same trick addDays/daysBetween already use — so a
// calendar date displays as the day the backend stored it. Timestamps that
// already carry a time are unaffected: toLocalDateInput resolves them to their
// local calendar date, which is what these formatters showed before.

export function formatDate(dateString: string): string {
  const date = new Date(toLocalDateInput(dateString) + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function formatDateShort(dateString: string): string {
  const date = new Date(toLocalDateInput(dateString) + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
}

export function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(toLocalDateInput(startDate) + 'T12:00:00');
  const end = new Date(toLocalDateInput(endDate) + 'T12:00:00');

  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return `${startStr} - ${endStr}`;
}

export function getDayOfWeek(dateString: string): string {
  const date = new Date(toLocalDateInput(dateString) + 'T12:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short' });
}

export function getDayNumber(dateString: string): number {
  const date = new Date(toLocalDateInput(dateString) + 'T12:00:00');
  return date.getDate();
}

export function getMonthYear(dateString: string): string {
  const date = new Date(toLocalDateInput(dateString) + 'T12:00:00');
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Get local date as YYYY-MM-DD string (for HTML date inputs)
 * Uses local timezone instead of UTC
 */
export function getLocalDateString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert ISO date string to local YYYY-MM-DD for date input
 * Handles both ISO strings and YYYY-MM-DD formats
 */
export function toLocalDateInput(dateString: string | undefined | null): string {
  if (!dateString) return '';
  // If already YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  // Parse ISO string and extract local date components
  const date = new Date(dateString);
  return getLocalDateString(date);
}

export function addDays(dateString: string, days: number): string {
  // Use noon to avoid DST issues when adding days
  const date = new Date(dateString + 'T12:00:00');
  date.setDate(date.getDate() + days);
  return getLocalDateString(date);
}

export function getDateRange(startDate: string, days: number): string[] {
  const dates: string[] = [];
  for (let i = 0; i < days; i++) {
    dates.push(addDays(startDate, i));
  }
  return dates;
}

export function isToday(dateString: string): boolean {
  return dateString === getTodayString();
}

export function isPast(dateString: string): boolean {
  return dateString < getTodayString();
}

export function isFuture(dateString: string): boolean {
  return dateString > getTodayString();
}

// The campground's own date. hcp-backend resolves "today" in the STORE's
// timezone (TimezoneService.get_store_today, default America/New_York), so a
// manager working from another timezone would otherwise see badges, "today"
// markers and date minimums that disagree with what the booking engine
// enforces. Set once at startup and on login; until then this stays null and
// every call falls back to the browser's own date, as it always did.
let storeTimeZone: string | null = null;

export function setStoreTimeZone(timeZone: string | null | undefined): void {
  storeTimeZone = timeZone || null;
}

export function getStoreTimeZone(): string | null {
  return storeTimeZone;
}

/**
 * Today's date (YYYY-MM-DD) at the store, falling back to the browser's date
 * when no store timezone is configured or the configured one is unusable.
 *
 * Note this deliberately does NOT change `getLocalDateString(date)`: that one
 * extracts the calendar day of a Date the caller already built, and addDays /
 * daysBetween / getMonthName rely on it round-tripping their local-noon
 * anchor. Re-reading those through a distant store timezone would shift the
 * day and break the arithmetic.
 */
export function getTodayString(): string {
  if (storeTimeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: storeTimeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());
      const get = (type: string) => parts.find(p => p.type === type)?.value;
      const year = get('year');
      const month = get('month');
      const day = get('day');
      if (year && month && day) {
        return `${year}-${month}-${day}`;
      }
    } catch {
      // Unknown/invalid IANA zone — fall through to the browser's date.
    }
  }
  return getLocalDateString();
}

export function daysBetween(startDate: string, endDate: string): number {
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  const diffTime = end.getTime() - start.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}

export function getSeasonStart(year?: number): string {
  const y = year || new Date().getFullYear();
  return `${y}-04-15`;
}

export function getSeasonEnd(year?: number): string {
  const y = year || new Date().getFullYear();
  return `${y}-10-15`;
}

export function getSeasonDays(year?: number): number {
  return daysBetween(getSeasonStart(year), getSeasonEnd(year));
}

export function getMonthName(dateString: string): string {
  const date = new Date(dateString + 'T12:00:00');
  return date.toLocaleDateString('en-US', { month: 'long' });
}

/**
 * Parse a backend timestamp that is UTC but serialized without a timezone.
 *
 * hcp-backend builds these with `datetime.utcnow().isoformat()`, which yields
 * "2026-08-05T19:34:00" — no trailing Z. JS reads that date-time form as
 * *local* time, so a 10-minute hold rendered on a UTC-5 machine looks like it
 * still has 5 hours left. Append the Z unless an offset is already present.
 */
export function parseBackendUtc(value: string): Date {
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasZone ? value : value + 'Z');
}

/**
 * Get local date and time as readable string
 * Example: "02/15/2026, 2:30 PM"
 */
export function getLocalDateTimeString(date: Date = new Date()): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}
